// Disable HMR for middleware to prevent Nuxt virtual module conflicts
import { am3Redirects, routeMapping } from '@audience-republic/router-config'
import { completeRouteMiddleware } from '@/plugins/ar-axios'
import { normalizePath } from '@/utils/normalizePath'

if (import.meta.hot) {
  import.meta.hot.decline()
}

/**
 * Match a path against a route pattern with dynamic params
 *
 * Examples:
 * - '/audience/12345/view' matches '/audience/[customer]/view'
 * - '/settings/profile' matches '/settings'
 */
function matchRoute(path, pattern) {
  // Exact match
  if (path === pattern) {
    return true
  }

  // Check if route is a prefix (without params)
  if (!pattern.includes('[') && path.startsWith(pattern + '/')) {
    return true
  }

  // Handle patterns with parameters [param]
  if (pattern.includes('[')) {
    // Convert pattern to regex by replacing [param] with capture groups
    const regexPattern = pattern
      .replace(/\//g, '\\/') // Escape slashes
      .replace(/\[([^\]]+)\]/g, '([^/]+)') // Replace [param] with capture group

    const regex = new RegExp(`^${regexPattern}(\\/.*)?$`)
    return regex.test(path)
  }

  return false
}

/**
 * Check if a path should be redirected within AM3 and return the target path
 * @param {string} path
 */
function getAM3Redirect(path) {
  for (const redirectConfig of am3Redirects) {
    const match = path.match(redirectConfig.pattern)
    if (match) {
      return redirectConfig.redirect(match)
    }
  }

  return null
}

/**
 * Get subdomain for a given path using route mapping
 */
function getSubdomainForRoute(path) {
  const cleanPath = normalizePath(path)
  // Try exact matching routes first
  for (const [routePatternRaw, subdomains] of Object.entries(routeMapping)) {
    const routePattern = normalizePath(routePatternRaw)
    if (matchRoute(cleanPath, routePattern)) {
      return subdomains[0] // Return first subdomain in the list
    }
  }

  // Default fallback
  return 'AM2'
}

// Define the middleware function
export default defineNuxtRouteMiddleware((to) => {
  // Use Nuxt composables only inside the middleware function
  const config = useRuntimeConfig()
  try {
    if (config.public.ENABLED_HYBRID_SETUP) {
      let subdomain = getSubdomainForRoute(to.path)
      console.log(subdomain)
      const optOutCookie = useCookie('am3EarlyAccessOptOut')
      const betaTesterCookie = useCookie('am3BetaTester')
      console.log('AM3 ROUTE MIDDLEWARE | opt-out:', optOutCookie.value, '| beta-tester:', betaTesterCookie.value)

      // Route to AM2 if user opted out, or if session is verified and beta-tester flag is absent.
      // When betaTesterCookie is absent (not yet verified), let through — authenticated middleware handles it.
      if (subdomain === 'AM3' && (optOutCookie.value === true || betaTesterCookie.value === false)) {
        subdomain = 'AM2'
      }

      // Check for AM3 internal redirects
      const redirectTarget = getAM3Redirect(to.path)
      if (redirectTarget) {
        console.log(`AM3 INTERNAL REDIRECT: ${to.path} -> ${redirectTarget}`)
        return navigateTo(redirectTarget, { redirectCode: 301 })
      }

      const expectedHost = config.public[subdomain + '_SUBDOMAIN']

      console.log(
        'AM3 ROUTE MIDDLEWARE',
        window.location.hostname + to.path,
        ' --> ',
        expectedHost + to.path,
      )

      if (import.meta.client && window.location.hostname !== expectedHost) {
        console.log(
          `REDIRECTING: from ${window.location.hostname} to ${expectedHost} for path ${to.fullPath}`,
        )
        return navigateTo(
          `${window.location.protocol}//${expectedHost}${to.fullPath}`,
          { external: true },
        )
      }
    }
  }
  finally {
    // Signal that the route middleware has completed its execution
    // This will allow API requests to proceed
    if (import.meta.client) {
      completeRouteMiddleware()
    }
  }
})
